import React, { useState, useEffect } from "react";
import {
  Table,
  Empty,
  Row,
  Drawer,
  Col,
  Modal,
  Divider,
  Button,
  Popconfirm,
  Tooltip,
  notification,
} from "antd";

import {
  InfoCircleOutlined,
  BarsOutlined,
  SendOutlined,
  RedoOutlined,
  CalendarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import RequestHandler from "../../util/RequestHandler";
import moment from "moment";

import BkmRegularPaymentAddForm from "../Forms/BkmRegularPaymentAddForm";
import BkmRegularPaymentTransactionTableFilter from "../Filters/BkmRegularPaymentTransactionTableFilter";
import { ColumnHelper } from "../../util/TableHelper";
import { useHistory } from "react-router-dom";

const BkmRegularPaymentTransaction = (props) => {
  const instructionSuccessMessage = "Talimat işleminiz alındı.";
  const [tableData, setTableData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [tableDataQueryOptions, setTableDataQueryOptions] = useState({
    skip: 0,
    take: 15,
    current: 1,
    filter: "",
  });

  const [addFormVisibility, setAddFormVisibility] = useState(false);
  const [addFormData, setAddFormData] = useState({});
  const [detailModal, setDetailModal] = useState({ visible: false });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [planDataByTransactionId, setPlanDataByTransactionId] = useState({});

  const history = useHistory();

  useEffect(() => getTableData(), [tableDataQueryOptions]);

  useEffect(() => {
    if (props.addButtonEvent > 0) {
      setAddFormVisibility(true);
    }
  }, [props.addButtonEvent]);

  useEffect(() => {
    const result = checkIsBkmComplete();
    if (result.isBkmComplete) {
      getDetailModalData(result.paymentId, result);
    }
  }, []);

  const checkIsBkmComplete = () => {
    const params = new URLSearchParams(window ? window.location.search : {});
    const isBkmComplete = params.get("isObComplete")?.toLowerCase() === "true";

    if (isBkmComplete) {
      const isSuccess = params.get("success")?.toLowerCase() === "true";
      const message = isSuccess
        ? instructionSuccessMessage
        : params.get("message");
      const notificationType = isSuccess ? "success" : "error";

      notification[notificationType]({
        message: message,
        duration: 5,
      });
      history.replace(window.location.pathname);

      return {
        isBkmComplete: isBkmComplete,
        paymentId: params.get("paymentId"),
        success: isSuccess,
        message: message,
        isSenderSaved: params.get("isSenderSaved")?.toLowerCase() === "true",
        isRecipientSaved:
          params.get("isRecipientSaved")?.toLowerCase() === "true",
      };
    }

    return { isBkmComplete: isBkmComplete };
  };

  const getFilterQuery = () => {
    const { filter } = tableDataQueryOptions;

    const conditions = [];
    let query = "";

    if (filter) {
      conditions.push(filter);
    }

    if (conditions.length !== 0) {
      query = "&$filter=" + conditions.join(" and ");
    }
    return query;
  };

  const getTableData = () => {
    setTableData({
      ...tableData,
      loading: true,
    });

    const { skip, take } = tableDataQueryOptions;

    RequestHandler.get(
      `/BkmRegularPaymentTransaction?$count=true${getFilterQuery()}&$skip=${skip}&$top=${take}`,
    ).then((response) => {
      setTableData({
        data: response.value || [],
        total: response["@odata.count"] || 0,
        loading: false,
      });
    });
  };

  const paging = (p) => {
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * p.pageSize,
        take: p.pageSize,
        current: p.current,
      };
    });
  };

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      skip: 0,
      current: 1,
      take: tableDataQueryOptions.take,
      filter: "",
    });
  };

  const onFinishTableFilter = (query) => {
    setTableDataQueryOptions((prevState) => {
      return { skip: 0, current: 1, take: prevState.take, filter: query };
    });
  };

  const onAddFormSuccess = () => {
    getTableData();
    setAddFormVisibility(false);
  };

  const getAddFormData = (row) => {
    setAddFormData(row);
    setAddFormVisibility(true);
  };

  const getDetailModalData = (row, bkmConsentResult) => {
    if (!row) return;

    if (typeof row === "string") {
      RequestHandler.get(`/BkmRegularPaymentTransaction/GetById/${row}`).then(
        (response) => {
          if (response) {
            setDetailModal({
              ...response,
              bkmConsentResult: bkmConsentResult,
              visible: true,
            });
          }
        },
      );

      return;
    }

    setDetailModal({
      ...row,
      bkmConsentResult: bkmConsentResult,
      visible: true,
    });
  };

  const getRowKey = (row) => row.id || row.Id;

  const normalizePlanData = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.value)) return response.value;
    if (Array.isArray(response?.data)) return response.data;

    return [];
  };

  const getRegularPaymentPlans = (row) => {
    const transactionId = getRowKey(row);

    if (!transactionId || planDataByTransactionId[transactionId]) return;

    setPlanDataByTransactionId((prevState) => ({
      ...prevState,
      [transactionId]: prevState[transactionId] || {
        data: [],
        loading: true,
      },
    }));

    RequestHandler.get(
      `/BkmRegularPaymentPlan?$filter=regularPaymentTransactionId eq ${transactionId}`,
    ).then((response) => {
      setPlanDataByTransactionId((prevState) => ({
        ...prevState,
        [transactionId]: {
          data: normalizePlanData(response),
          loading: false,
        },
      }));
    });
  };

  const expandRow = (row) => {
    const rowKey = getRowKey(row);

    setExpandedRowKeys((prevState) =>
      prevState.includes(rowKey) ? prevState : [...prevState, rowKey],
    );
    getRegularPaymentPlans(row);
  };

  const handleOnClickDelete = (id) => {
    RequestHandler.delete(`/BkmRegularPaymentTransaction/${id}`).then(
      (response) => {
        if (response.success) {
          getTableData();
        }
      },
    );
  };

  const getIdentityType = (type) => {
    const identityTypes = {
      V: "Vergi No",
      K: "T.C. Kimlik No",
      M: "Müşteri No",
      Y: "Yabancı Kimlik No",
      P: "Pasaport No",
    };

    return identityTypes[type] || type;
  };

  const getPaymentPurpose = (type) => {
    const paymentPurposes = {
      "01": "Konut Kirası Ödemesi",
      "02": "İş yeri Kirası Ödemesi",
      "03": "Diğer Kira Ödemesi",
      "04": "E-Ticaret Ödemesi: ",
      "05": "Çalışan Ödemesi",
      "06": "Ticari ödeme",
      "07": "Bireysel Ödeme",
      "08": "Yatırım: ",
      "09": "Finansal",
      10: "Eğitim ödemesi",
      11: "Aidat ödemesi",
      12: "Gayrimenkul alım ödemesi",
      13: "Motorlu taşıt alım ödemesi",
      14: "Borç verme/Borç ödeme",
      15: "Hediye/bağış/yardım",
      16: "Vergi/resim/harç ödemesi",
      17: "Tazminat/sigorta ödemesi",
      18: "Avukatlık/Danışmanlık/Müşavirlik Ödemesi",
      19: "Sağlık ödemesi",
      20: "Kripto/dijital varlık",
      21: "Şans oyunları/bahis ödemesi",
      22: "Eğlence/sosyal medya ödemesi",
    };

    return paymentPurposes[type] || type;
  };

  const getPaymentPeriod = (type) => {
    const paymentPeriods = {
      D: "Günlük",
      W: "Haftalık",
      M: "Aylık",
      Y: "Yıllık",
      G: "Günlük",
      H: "Haftalık",
      A: "Aylık",
      YIL: "Yıllık",
    };

    return paymentPeriods[type] || type;
  };

  const getPlanPaymentStatus = (type) => {
    const paymentStatuses = {
      "01": "Gerçekleşti",
      "02": "Gönderildi",
      "03": "Gerçekleşmedi",
      "04": "Onayda bekliyor",
      "05": "Ödeme Bekleniyor",
      "06": "Kısmen Gerçekleşti",
      "07": "İptal Edildi",
    };

    return paymentStatuses[type] || type || "-";
  };

  const renderDate = (data, format = "DD.MM.YYYY") => {
    if (!data) return "-";

    const dateFormat = typeof format === "string" ? format : "DD.MM.YYYY";
    const date = moment(data);
    return date.isValid() ? date.format(dateFormat) : "-";
  };

  const renderDecimal = (data) => {
    if (data === null || data === undefined) return "-";

    return ColumnHelper.renderDecimal(data);
  };

  const getPlanValue = (row, keys) => {
    const key = keys.find(
      (item) => row[item] !== undefined && row[item] !== null,
    );

    return key ? row[key] : undefined;
  };

  const renderPlanValue = (data) => {
    return data === null || data === undefined || data === "" ? "-" : data;
  };

  const columns = [
    {
      title: "Hesap Sağlayıcı",
      dataIndex: "hhsName",
      render: (data, row) => data || row.bankName || "-",
    },
    {
      title: "İşlem Tarihi",
      dataIndex: "addDate",
      align: "center",
      render: (data) => renderDate(data, "DD.MM.YYYY HH:mm"),
    },
    {
      title: "Gönderen Ünvan",
      dataIndex: "senderName",
    },
    {
      title: "Gönderen Iban",
      dataIndex: "senderIban",
    },
    {
      title: "Alıcı Ünvan",
      dataIndex: "recipientName",
    },
    {
      title: "Alıcı Iban",
      dataIndex: "recipientIban",
    },
    {
      title: "Talimat Tutarı",
      dataIndex: "instructionAmount",
      align: "right",
      render: renderDecimal,
    },
    {
      title: "Para Birimi",
      dataIndex: "instructionCurrencyCode",
      align: "center",
    },
    {
      title: "İlk Ödeme",
      dataIndex: "firstPaymentDate",
      align: "center",
      render: (data) => renderDate(data),
    },
    {
      title: "Son Ödeme",
      dataIndex: "lastPaymentDate",
      align: "center",
      render: (data) => renderDate(data),
    },
    {
      title: "Ödeme Sayısı",
      dataIndex: "paymentCount",
      align: "center",
    },
    {
      title: "Periyot",
      dataIndex: "paymentPeriodCode",
      align: "center",
      render: getPaymentPeriod,
    },
    {
      title: "Ödeme Amacı",
      dataIndex: "paymentPurpose",
      render: getPaymentPurpose,
    },
    {
      title: "Detay",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (_, row) => (
        <a onClick={() => getDetailModalData(row)}>
          <InfoCircleOutlined style={{ fontSize: "130%" }} />
        </a>
      ),
    },
    {
      title: "Tekrarla",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (_, row) => (
        <a onClick={() => getAddFormData(row)}>
          <RedoOutlined style={{ fontSize: "130%" }} />
        </a>
      ),
    },
    {
      title: "Ödeme Planı",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (_, row) => (
        <a onClick={() => expandRow(row)}>
          <CalendarOutlined style={{ fontSize: "130%" }} />
        </a>
      ),
    },
    {
      title: "İptal",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (data, row) => (
        <Popconfirm
          title="Düzenli ödeme emrini iptal etmek istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          onConfirm={() => handleOnClickDelete(data || row.Id)}
        >
          <Tooltip title="Düzenli Ödeme Emri İptali">
            <a>
              <DeleteOutlined style={{ color: "red", fontSize: "130%" }} />
            </a>
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const planColumns = [
    {
      title: "Ödeme Sıra No",
      align: "center",
      render: (_, row) =>
        renderPlanValue(
          getPlanValue(row, ["paymentSequenceNumber", "PaymentSequenceNumber"]),
        ),
    },
    {
      title: "Ödeme Tutar",
      align: "right",
      render: (_, row) =>
        renderDecimal(getPlanValue(row, ["paymentAmount", "PaymentAmount"])),
    },

    {
      title: "Para Birimi",
      align: "center",
      render: (_, row) =>
        renderPlanValue(getPlanValue(row, ["currencyCode", "CurrencyCode"])),
    },
    {
      title: "Ödeme Talimat Tarihi",
      align: "center",
      render: (_, row) =>
        renderDate(getPlanValue(row, ["instructionDate", "InstructionDate"])),
    },
    {
      title: "Gerçekleşen Ödeme Tutar",
      align: "right",
      render: (_, row) =>
        renderDecimal(
          getPlanValue(row, ["actualPaymentAmount", "ActualPaymentAmount"]),
        ),
    },
    {
      title: "Ödeme Gerçekleşme Zamanı",
      align: "center",
      render: (_, row) =>
        renderDate(
          getPlanValue(row, ["executionDateTime", "ExecutionDateTime"]),
          "DD.MM.YYYY HH:mm",
        ),
    },
    {
      title: "Ödeme Durumu",
      align: "center",
      render: (_, row) =>
        getPlanPaymentStatus(
          getPlanValue(row, ["paymentStatus", "PaymentStatus"]),
        ),
    },
  ];

  const renderDetailItem = (label, value) => (
    <Col xs={24} md={12} lg={8} style={{ marginBottom: 10 }}>
      <div
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #edf0f2",
          padding: "8px 10px",
          minHeight: 56,
        }}
      >
        <div
          style={{
            color: "#8c8c8c",
            fontSize: 12,
            lineHeight: "18px",
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            color: "#1f1f1f",
            fontWeight: 500,
            lineHeight: "20px",
            wordBreak: "break-word",
          }}
        >
          {value || "-"}
        </div>
      </div>
    </Col>
  );

  const renderExpandedRow = (row) => {
    const rowKey = getRowKey(row);
    const planState = planDataByTransactionId[rowKey] || {
      data: [],
      loading: false,
    };
    const sortedPlanData = [...planState.data].sort((firstItem, secondItem) => {
      const firstSequenceNumber = Number(
        getPlanValue(firstItem, [
          "paymentSequenceNumber",
          "PaymentSequenceNumber",
        ]),
      );
      const secondSequenceNumber = Number(
        getPlanValue(secondItem, [
          "paymentSequenceNumber",
          "PaymentSequenceNumber",
        ]),
      );

      if (Number.isNaN(firstSequenceNumber)) return 1;
      if (Number.isNaN(secondSequenceNumber)) return -1;

      return firstSequenceNumber - secondSequenceNumber;
    });

    return (
      <div
        style={{
          backgroundColor: "#fafafa",
          borderTop: "1px solid #e8e8e8",
          padding: "14px 16px 10px",
        }}
      >
        <div
          style={{
            color: "#262626",
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          Talimat Bilgileri
        </div>
        <Row gutter={12}>
          {renderDetailItem("Talimat No", row.instructionId)}
          {renderDetailItem("Talimat Rumuzu", row.instructionAlias)}
          {renderDetailItem(
            "Ödeme Amacı",
            getPaymentPurpose(row.paymentPurpose),
          )}
          {renderDetailItem("Periyot", getPaymentPeriod(row.paymentPeriodCode))}
          {renderDetailItem(
            "İlk Ödeme Tarihi",
            renderDate(row.firstPaymentDate),
          )}
          {renderDetailItem(
            "Son Ödeme Tarihi",
            renderDate(row.lastPaymentDate),
          )}
          <Col span={24} style={{ marginBottom: 6 }}>
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #edf0f2",
                padding: "8px 10px",
              }}
            >
              <div
                style={{
                  color: "#8c8c8c",
                  fontSize: 12,
                  lineHeight: "18px",
                  marginBottom: 2,
                }}
              >
                Açıklama
              </div>
              <div
                style={{
                  color: "#1f1f1f",
                  fontWeight: 500,
                  lineHeight: "20px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {row.description || "-"}
              </div>
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: "12px 0" }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <span style={{ color: "#262626", fontWeight: 600 }}>Ödeme Planı</span>
          {!planState.loading && (
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
              {sortedPlanData.length} kayıt
            </span>
          )}
        </div>
        <div style={{ backgroundColor: "#ffffff" }}>
          <Table
            columns={planColumns}
            dataSource={sortedPlanData}
            rowKey={(plan, index) => plan.id || plan.Id || index}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            loading={planState.loading}
            pagination={false}
            size="small"
          />
        </div>
      </div>
    );
  };

  const width = window.innerWidth;
  const breakpoint = 480;
  const textRightClass = width < breakpoint ? "" : "gx-text-right";
  return (
    <>
      <Row align="middle">
        <Col xs={16}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <BarsOutlined /> DÜZENLİ PARA TRANSFERİ
          </p>
        </Col>
        <Col xs={8}>
          <Row justify="end" align="middle" style={{ paddingRight: 16 }}>
            <Button
              type="primary"
              onClick={() => setAddFormVisibility(true)}
              style={{ marginBottom: 0 }}
            >
              <SendOutlined /> Ödeme Başlat
            </Button>
          </Row>
        </Col>
      </Row>

      <BkmRegularPaymentTransactionTableFilter
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Row id="tableArea">
        <Col span={24}>
          <Table
            columns={columns}
            expandable={{
              expandedRowKeys: expandedRowKeys,
              expandedRowRender: renderExpandedRow,
              onExpand: (expanded, row) => {
                const rowKey = getRowKey(row);

                setExpandedRowKeys((prevState) =>
                  expanded
                    ? [...prevState, rowKey]
                    : prevState.filter((item) => item !== rowKey),
                );

                if (expanded) {
                  getRegularPaymentPlans(row);
                }
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%", width: "100%" }}
            rowKey={getRowKey}
            pageable={true}
            onChange={paging}
            pagination={{
              defaultPageSize: tableDataQueryOptions.take,
              pageSizeOptions: ["15", "25", "50", "100"],
              total: tableData.total,
              current: tableDataQueryOptions.current,
            }}
            dataSource={tableData.data}
            loading={tableData.loading}
            size="small"
          />
        </Col>
      </Row>

      <Drawer
        id="addDrawer"
        title="Ödeme Başlatma"
        width={500}
        onClose={() => {
          setAddFormVisibility(false);
          setAddFormData({});
        }}
        visible={addFormVisibility}
      >
        <BkmRegularPaymentAddForm
          onSuccess={onAddFormSuccess}
          data={addFormData}
        />
      </Drawer>

      <Modal
        id="detailModal"
        title={`${detailModal.hhsName || detailModal.bankName || ""} Rıza Bilgisi`}
        visible={detailModal.visible}
        onCancel={() => {
          setDetailModal({ visible: false });
        }}
        cancelButtonProps={{ className: "gx-d-none" }}
        onOk={() => {
          setDetailModal({ visible: false });
        }}
        width={1000}
      >
        {detailModal.bkmConsentResult && (
          <>
            <Row>
              <Col span={24}>
                <p>{detailModal.bkmConsentResult.message}</p>
              </Col>
            </Row>
            <Divider />
          </>
        )}

        <Divider>İzin Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.consentId}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            İşlem Tarihi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {renderDate(detailModal.addDate, "DD.MM.YYYY HH:mm")}
          </Col>
        </Row>

        <Divider>Kimlik Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getIdentityType(detailModal.identityType)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.identityValue}
          </Col>
        </Row>
        {detailModal.corporateIdentityValue && (
          <>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik Türü :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {getIdentityType(detailModal.corporateIdentityType)}
              </Col>
            </Row>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik No :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {detailModal.corporateIdentityValue}
              </Col>
            </Row>
          </>
        )}

        <Divider>Gönderen Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Gönderen Ünvan :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.senderName}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Gönderen Iban :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.senderIban}
          </Col>
        </Row>

        <Divider>Alıcı Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Alıcı Ünvan :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.recipientName}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Alıcı Iban :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.recipientIban}
          </Col>
        </Row>

        <Divider>Talimat Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Talimat No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.instructionId}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Talimat Rumuzu :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.instructionAlias}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Talimat Tutarı :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {renderDecimal(detailModal.instructionAmount)}{" "}
            {detailModal.instructionCurrencyCode}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            İlk Ödeme Tarihi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {renderDate(detailModal.firstPaymentDate)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Son Ödeme Tarihi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {renderDate(detailModal.lastPaymentDate)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Ödeme Sayısı :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.paymentCount}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Periyot :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getPaymentPeriod(detailModal.paymentPeriodCode)}
          </Col>
        </Row>

        <Divider>Ödeme Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Referans Bilgisi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.referenceNumber}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Ödeme Amacı :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getPaymentPurpose(detailModal.paymentPurpose)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Açıklama :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.description}
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default BkmRegularPaymentTransaction;
